import { useEffect, useRef } from 'react'
import { getAllCustomViews } from '@/db/viewerDb'
import { useAppStore } from '@/store/appStore'
import { useContentBundle } from '@/hooks/useContentBundle'
import { reloadCustomViews } from '@/services/customViewsSync'
import { isOwnedView } from '@/utils/customViews'

/**
 * Reads custom views from IndexedDB (populated by useInitialSync) into
 * the Zustand store. Falls back to whatever is cached when offline.
 *
 * The actual API fetch + store population happens in useInitialSync.
 * This hook handles the initial load from IndexedDB on mount (e.g. on
 * page refresh when the store has been reset but IndexedDB still has data).
 */
export function useCustomViews() {
  const setCustomViews = useAppStore((s) => s.setCustomViews)
  const customViews = useAppStore((s) => s.customViews)
  const userDataSyncVersion = useAppStore((s) => s.userDataSyncVersion)
  const isOffline = useAppStore((s) => s.isOffline)
  const { data: bundle } = useContentBundle()
  // Unknown until a bundle loads; shared views are only stripped on an explicit "off".
  const sharingDisabled = bundle !== undefined && !bundle.sharedCustomViewsEnabled

  useEffect(() => {
    // Re-read from IndexedDB whenever the sync version bumps (after initial sync)
    // or on first mount if the store is empty
    getAllCustomViews().then((views) => {
      if (views.length > 0) {
        setCustomViews(views)
      }
    })
  }, [userDataSyncVersion, setCustomViews])

  // Shared views can still arrive from IndexedDB or a sync that predates the setting change.
  useEffect(() => {
    if (sharingDisabled && customViews.some((cv) => !isOwnedView(cv))) {
      setCustomViews(customViews.filter(isOwnedView))
    }
  }, [sharingDisabled, customViews, setCustomViews])

  const wasDisabled = useRef(false)
  useEffect(() => {
    if (wasDisabled.current && !sharingDisabled && !isOffline) {
      reloadCustomViews().catch(() => undefined)
    }
    wasDisabled.current = sharingDisabled
  }, [sharingDisabled, isOffline])

  return customViews
}
